import { connect } from 'pwa-helpers';
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { setProductAsync, selectProduct } from './product';
import { Product } from './product.interface';
import './product-card-display';
import { ProductCardData } from './product-card-display';
import variables from '../../common/styles/variables';
import { getImageSrcUrl, getPdpLink } from '../../common/utils/product-util';
import { mapCatalog } from '../../common/utils/util';

/**
 * An element to add products to your cart.
 *
 * @csspart large-card - The container of the large-card variation
 * @csspart small-card - The container of the small-card variation
 * @csspart image - Available in the small-card and large-card variations.
 * @csspart heading - Available in the large-card and small-card variations.
 * @csspart header - Available in the large-card variation.
 * @csspart details - Available in the large-card variation.
 * @csspart add-to-cart - Available in the large-card variation.
 * @csspart link - Available in the large-card variation.
 */
@customElement('mhecomm-product-card')
export class ProductCard extends connect(store)(LitElement) {
  static styles = [
    variables,
    css`
      :host {
        display: block;
      }
    `,
  ];

  /**
   * The heading level of the heading text. This should be h1 - h6. If no value is provided or an improper value is provided, h3 will be used.
   */
  @property()
  headingLevel = 'h3';

  /**
   * The ISBN of the product to display
   */
  @property()
  isbn = '';

  /**
   * The business unit of the product to display, such as 'he', or 'seg'.
   */
  @property()
  businessUnit = '';

  /**
   * The variation of the product card to display in different variations or styles, the values can be small-card, large-card.
   */
  @property()
  variation = 'small-card';

  /**
   * The text that can be configured for the button to display, such as 'Add to Cart', 'Rent Now'
   */
  @property()
  text = 'ADD TO CART';

  @state()
  private product?: ProductCardData | undefined;

  render() {
    return html`
      <mhecomm-product-card-display
        headingLevel=${this.headingLevel}
        exportparts="heading, container, image, header, details, add-to-cart, link"
        variation=${this.variation}
        text=${this.text}
        .product="${this.product}"
      >
      </mhecomm-product-card-display>
    `;
  }

  update(changedProperties: Map<string, string>) {
    super.update(changedProperties);

    if (changedProperties.has('businessUnit') || changedProperties.has('isbn')) {
      this.updateProduct();
    }
  }

  stateChanged(state: StoreState) {
    this.product = this.getProductCardData(selectProduct(state, this.isbn));
  }

  getProductCardData(product: Product | undefined): ProductCardData | undefined {
    return product !== undefined
      ? {
          kind: 'ProductCardData',
          title: product.title,
          image: getImageSrcUrl(product.image || product.isbn),
          copyRightYear: product.copyrightYear,
          businessUnit: this.businessUnit,
          isbn: product.isbn,
          isbn13: product.isbn13,
          netPrice: product.NET_PRICE,
          url: getPdpLink({
            businessUnit: this.businessUnit,
            isbn: product.isbn,
          }),
          contributors: product.contributors,
          description: product.PDP_DESCRIPTION,
          gradeLevels: product.gradeLevels,
          instructorOnly: product.instructorOnly,
        }
      : undefined;
  }

  private updateProduct() {
    if (this.businessUnit && this.isbn) {
      store.dispatch(setProductAsync(mapCatalog(this.businessUnit), this.isbn));
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-product-card': ProductCard;
  }
}
